/* eslint-disable security/detect-object-injection */
import { call, put, select } from 'redux-saga/effects'
import { API_RESPONSE_TYPE, IApiResponse } from 'shared/constants'
import { NotificationType } from 'pages/Notification/Notification.types'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { getErrorMessage } from 'services/Api/Errors'
import { IAppState } from 'infra/AppState'
import { getAbProfileV1Api } from 'services/Api/abServiceApi'
import { Action } from 'infra/types'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { getOrCreateWebDeviceId } from 'services/Api/DeviceApi'
import { getStaffBusinessSettingsApi } from 'services/Api/BillingApiFolder.ts/api'
import {
  getFormattedAbExperiment,
  getFormattedAbProfile,
  getFormattedBusinessSettings
} from './formatter'
import {
  getAbProfileFail,
  getAbProfileSuccess,
  getStaffBusinessSettingsSuccess
} from './experiment.action'

export function* getAbProfileSagaEffects() {
  try {
    const { merchantProfile } = yield select((state: IAppState) => {
      return state.Dashboard
    })
    const response: IApiResponse = yield call(getAbProfileV1Api, merchantProfile?.data?.id)
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      const formattedProfile = getFormattedAbProfile(response.data)
      yield put(getAbProfileSuccess(formattedProfile, getFormattedAbExperiment(response.data)))
      Mixpanel.setMixpanelEnabled(formattedProfile.isMixpanelWhitelisting)
      // Re-identify and register since these were called before AB flag was set
      if (formattedProfile.isMixpanelWhitelisting) {
        const merchantId = merchantProfile?.data?.id
        if (merchantId) {
          Mixpanel.identify(merchantId)
          Mixpanel.register({ merchant_id: merchantId })
          Mixpanel.people.set({ merchant_id: merchantId })
        }
        // Attach the stable per-browser device id as a super property (Android
        // parity: the legacy Android app registered `okc_device_id` too). It's the
        // same client-generated UUID used for device/push registration; identity
        // stays keyed on merchant_id above — this is only device correlation.
        Mixpanel.register({ okc_device_id: getOrCreateWebDeviceId() })
        // Stamp every event with the platform ('Desktop' / 'MobileWeb'), the same
        // key and value trackWithPreset adds per event. As a super property it also
        // covers plain Mixpanel.track and the pushEvent saga (BWR_*, upi_qr_*, ...).
        // `generated_by` is Android's key for the same thing (it sends
        // 'android_app'), so shared dashboards can split both apps on one property.
        Mixpanel.register({ Platform: Mixpanel.platform, generated_by: 'web' })
        // Replay queued events after identity is set so they have user context
        Mixpanel.flushPendingEvents()
      }
    } else {
      const message = response.data?.error
      yield put(getAbProfileFail(message || 'An Error Occurred'))
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
  }
}

export function* getStaffBusinessSettingsEffects(action: Action<string>) {
  try {
    const businessId = action.payload
    const response: IApiResponse = yield call(getStaffBusinessSettingsApi, businessId)
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      yield put(
        getStaffBusinessSettingsSuccess(getFormattedBusinessSettings(response.data.settings))
      )
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
  }
}
